import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites } from "../api/admin-api.js";
import type {
  ActivePriceRow,
  AdminRole,
  FloorVersion,
  LadderVersion,
  PriceVersion,
  RouterFeeSchedule,
  RouterFeeTier,
  TransferVersion,
} from "../api/types.js";
import { MutationForm, fieldError } from "../forms/mutation-form.js";
import { DayPartsPanel } from "./day-parts.js";
import { VersionedPanel } from "./versioned-panel.js";
import { bpToSun, bps, centiSunToSun, count, fromUtcInput, trx, utc } from "../format.js";
import {
  Address,
  Blank,
  Button,
  ErrorNote,
  Field,
  inputClass,
  Note,
  Panel,
  Pill,
  ScreenHeader,
  Spinner,
  Table,
  Td,
  Th,
} from "../ui.js";

/**
 * Every runtime price, its history, and the forms that publish the next version.
 *
 * PRICES ARE VERSIONED, NEVER EDITED (b2b-pricing.md §1, migration 0003 rejects an UPDATE at
 * the database). There is no edit control anywhere on this screen: a mistake is corrected by
 * publishing another version, which is why every table here is a history and not a value.
 *
 * FIVE SURFACES, FIVE SCALES, AND THE COLUMN HEAD SAYS WHICH. Retail is SUN per unit, the
 * floor is centi-SUN per unit, the transfer price and a contract price are basis points of one
 * SUN, and the ladder's parameters are basis points of a rate. They are shown converted, with
 * the raw integer in the title attribute, because the form posts the integer.
 */

// Every rental term, shortest first (core TIER_IDS, owner 2026-11-01).
const TIERS = ["5m", "10m", "15m", "30m", "1h", "3h", "6h", "12h", "1d", "2d", "3d", "7d", "14d", "30d"] as const;

export function PricesScreen({ role }: { role: AdminRole }) {
  return (
    <>
      <ScreenHeader
        title="Prices"
        lead="Every price on this platform lives in an append-only table with an effective-from, a changed-by and a reason. Nothing here can be edited; a new version supersedes the old one."
      />
      <div class="space-y-3">
        <RetailPanel role={role} />
        <DayPartsPanel role={role} />
        <FloorPanel role={role} />
        <RouterFeePanel role={role} />
        <TransferPanel role={role} />
        <LadderPanel role={role} />
      </div>
    </>
  );
}

// ---- retail grid ------------------------------------------------------------------------------

function RetailPanel({ role }: { role: AdminRole }) {
  const active = useResource((signal) => {
    void signal;
    return adminReads.activePrices();
  }, []);
  const versions = useResource((signal) => {
    void signal;
    return adminReads.priceVersions({ limit: 50 });
  }, []);

  return (
    <VersionedPanel
      title="Retail grid"
      subtitle="What the public pages quote. A retail version needs at least 60 s of lead time, so a fat-fingered public price can be superseded before anybody is quoted at it."
      role={role}
      formLabel="Publish a retail version"
      renderForm={(done, cancel) => (
        <RetailForm
          onDone={() => {
            active.reload();
            versions.reload();
            done();
          }}
          onCancel={cancel}
        />
      )}
    >
      {active.loading ? (
        <Spinner />
      ) : active.error !== null ? (
        <ErrorNote error={active.error} onRetry={() => active.reload()} />
      ) : (
        <ActiveRetailTable rows={active.data?.data ?? []} />
      )}
      <h3 class="mt-4 mb-1 num text-[10.5px] font-medium uppercase tracking-[0.08em] text-ink-muted">
        History
      </h3>
      {versions.loading ? (
        <Spinner />
      ) : versions.error !== null ? (
        <ErrorNote error={versions.error} onRetry={() => versions.reload()} />
      ) : (
        <RetailHistoryTable rows={versions.data?.data ?? []} />
      )}
    </VersionedPanel>
  );
}

function ActiveRetailTable({ rows }: { rows: ActivePriceRow[] }) {
  return (
    <Table
      rows={rows.length}
      empty="No retail row is in force. The API falls back to its money defaults, which nobody approved as a price."
      head={
        <tr>
          <Th>Resource</Th>
          <Th>Tier</Th>
          <Th>Day part</Th>
          <Th numeric>Base (SUN/unit)</Th>
          <Th numeric>Day-part factor</Th>
          <Th numeric>Brand markup</Th>
          <Th numeric>Min / max</Th>
          <Th>Visible</Th>
          <Th>Sales</Th>
          <Th>Effective from</Th>
        </tr>
      }
    >
      {rows.map((row) => (
        <tr key={row.id}>
          <Td>{row.resource}</Td>
          <Td>{row.tier}</Td>
          <Td>{row.day_part ?? <Blank title="Every day part" />}</Td>
          <Td numeric>{count(row.base_price_sun_per_unit)}</Td>
          <Td numeric>{bps(row.day_part_factor_bp)}</Td>
          <Td numeric>{bps(row.brand_markup_bp)}</Td>
          <Td numeric>
            {row.min_amount === null ? "—" : count(row.min_amount)} /{" "}
            {row.max_amount === null ? "—" : count(row.max_amount)}
          </Td>
          <Td>{row.visible ? "yes" : "no"}</Td>
          <Td>
            <Pill value={row.sales_paused ? "paused" : "active"} />
          </Td>
          <Td mono>{utc(row.effective_at)}</Td>
        </tr>
      ))}
    </Table>
  );
}

function RetailHistoryTable({ rows }: { rows: PriceVersion[] }) {
  return (
    <Table
      rows={rows.length}
      empty="No version has ever been published."
      head={
        <tr>
          <Th>Effective from</Th>
          <Th>Resource / tier</Th>
          <Th numeric>Base</Th>
          <Th numeric>Markup</Th>
          <Th>Volume grid</Th>
          <Th>Changed by</Th>
          <Th>Reason</Th>
        </tr>
      }
    >
      {rows.map((row) => (
        <tr key={row.id}>
          <Td mono>{utc(row.effective_from)}</Td>
          <Td>
            {row.resource} / {row.tier}
            {row.day_part === null ? "" : ` / ${row.day_part}`}
          </Td>
          <Td numeric>{count(row.base_price_sun_per_unit)}</Td>
          <Td numeric>{bps(row.brand_markup_bp)}</Td>
          <Td>
            {row.volume_grid === null || row.volume_grid.length === 0 ? (
              <Blank />
            ) : (
              row.volume_grid
                .map((entry) => `${count(entry.min_amount)}→${entry.price_sun_per_unit}`)
                .join(", ")
            )}
          </Td>
          <Td>{row.changed_by === null ? <Blank /> : <Address value={row.changed_by} />}</Td>
          <Td>{row.reason ?? <Blank />}</Td>
        </tr>
      ))}
    </Table>
  );
}

function RetailForm({ onDone, onCancel }: { onDone(): void; onCancel(): void }) {
  const [resource, setResource] = useState("energy");
  const [tier, setTier] = useState<string>("1h");
  const [dayPart, setDayPart] = useState("");
  const [base, setBase] = useState("");
  const [markup, setMarkup] = useState("");
  const [effectiveAt, setEffectiveAt] = useState("");
  const [salesPaused, setSalesPaused] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  return (
    <MutationForm
      title="Publish a retail price version"
      description="A new row in `price_config`. The active row is the latest whose effective-from has passed; quotes already issued keep their price until their TTL."
      changes={[
        { label: "resource / tier", before: null, after: `${resource} / ${tier}${dayPart === "" ? "" : ` / ${dayPart}`}` },
        { label: "base_price_sun_per_unit", before: null, after: base === "" ? "—" : base },
        ...(markup === "" ? [] : [{ label: "brand_markup_bp", before: null, after: markup }]),
        ...(salesPaused ? [{ label: "sales_paused", before: "false", after: "true" }] : []),
      ]}
      effect={
        effectiveAt === ""
          ? "60 seconds from now — the minimum lead time the API enforces for the retail grid."
          : `${effectiveAt.replace("T", " ")} UTC. The API refuses anything less than 60 s out.`
      }
      submitLabel="Publish version"
      busy={busy}
      disabled={base === ""}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        setBusy(true);
        setError(null);
        void adminWrites
          .publishPrice({
            resource,
            tier,
            ...(dayPart === "" ? {} : { day_part: dayPart }),
            base_price_sun_per_unit: Number(base),
            ...(markup === "" ? {} : { brand_markup_bp: Number(markup) }),
            ...(effectiveAt === "" ? {} : { effective_at: fromUtcInput(effectiveAt) ?? "" }),
            sales_paused: salesPaused,
            reason,
          })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <Field label="Resource" error={fieldError(error, "resource")}>
        <select
          class={inputClass}
          name="resource"
          value={resource}
          onChange={(event) => setResource((event.currentTarget as HTMLSelectElement).value)}
        >
          <option value="energy">energy</option>
          <option value="bandwidth">bandwidth</option>
        </select>
      </Field>
      <Field label="Tier" error={fieldError(error, "tier")}>
        <select
          class={inputClass}
          name="tier"
          value={tier}
          onChange={(event) => setTier((event.currentTarget as HTMLSelectElement).value)}
        >
          {TIERS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </Field>
      <Field
        label="Base price (SUN per unit)"
        hint="A whole number of SUN, as the table stores it."
        error={fieldError(error, "base_price_sun_per_unit")}
      >
        <input
          class={inputClass}
          name="base_price_sun_per_unit"
          type="number"
          min={1}
          value={base}
          onInput={(event) => setBase((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Brand markup (bp, optional)"
        hint="10,000 = ×1.00. The API accepts 10,000–20,000."
        error={fieldError(error, "brand_markup_bp")}
      >
        <input
          class={inputClass}
          name="brand_markup_bp"
          type="number"
          min={10_000}
          max={20_000}
          value={markup}
          onInput={(event) => setMarkup((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field label="Day part (optional)" error={fieldError(error, "day_part")}>
        <input
          class={inputClass}
          name="day_part"
          value={dayPart}
          onInput={(event) => setDayPart((event.currentTarget as HTMLInputElement).value.trim())}
        />
      </Field>
      <Field
        label="Effective from (UTC)"
        hint="Left empty: 60 s from now. This control has no time zone — what you type is read as UTC."
        error={fieldError(error, "effective_at")}
      >
        <input
          class={inputClass}
          name="effective_at"
          type="datetime-local"
          value={effectiveAt}
          onInput={(event) => setEffectiveAt((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Sales paused"
        hint="Ticked: stop selling this row from the new version's effective time."
        error={fieldError(error, "sales_paused")}
      >
        <input
          class="mt-1 size-4 align-middle"
          type="checkbox"
          name="sales_paused"
          checked={salesPaused}
          onChange={(event) => setSalesPaused((event.currentTarget as HTMLInputElement).checked)}
        />
      </Field>
    </MutationForm>
  );
}

// ---- floor grid -------------------------------------------------------------------------------

function FloorPanel({ role }: { role: AdminRole }) {
  const floor = useResource((signal) => {
    void signal;
    return adminReads.floorPrices({ limit: 50 });
  }, []);

  return (
    <VersionedPanel
      title="Floor grid"
      subtitle="The price nothing is sold below. `live_then_static` prefers the CatFee feed and falls back to this grid; `static_only` uses the grid alone."
      role={role}
      formLabel="Publish a floor version"
      renderForm={(done, cancel) => (
        <FloorForm
          onDone={() => {
            floor.reload();
            done();
          }}
          onCancel={cancel}
        />
      )}
    >
      {floor.loading ? (
        <Spinner />
      ) : floor.error !== null ? (
        <ErrorNote error={floor.error} onRetry={() => floor.reload()} />
      ) : (
        <>
          <FloorTable rows={floor.data?.active ?? []} empty="No floor row is in force." />
          <h3 class="mt-4 mb-1 num text-[10.5px] font-medium uppercase tracking-[0.08em] text-ink-muted">
            History
          </h3>
          <FloorTable
            rows={floor.data?.versions ?? []}
            empty="No floor version has ever been published."
          />
        </>
      )}
    </VersionedPanel>
  );
}

function FloorTable({ rows, empty }: { rows: FloorVersion[]; empty: string }) {
  return (
    <Table
      rows={rows.length}
      empty={empty}
      head={
        <tr>
          <Th>Effective from</Th>
          <Th>Resource / tier</Th>
          <Th>Day part</Th>
          <Th numeric>Floor</Th>
          <Th>Mode</Th>
          <Th>Changed by</Th>
          <Th>Reason</Th>
        </tr>
      }
    >
      {rows.map((row) => (
        <tr key={row.id}>
          <Td mono>{utc(row.effective_from)}</Td>
          <Td>
            {row.resource} / {row.tier}
          </Td>
          <Td>{row.day_part ?? <Blank title="Every day part" />}</Td>
          <Td numeric>
            <span title={`${row.price_centi_sun_per_unit} centi-SUN per unit`}>
              {centiSunToSun(row.price_centi_sun_per_unit)}
            </span>
          </Td>
          <Td>{row.mode}</Td>
          <Td>{row.changed_by === null ? <Blank /> : <Address value={row.changed_by} />}</Td>
          <Td>{row.reason ?? <Blank />}</Td>
        </tr>
      ))}
    </Table>
  );
}

function FloorForm({ onDone, onCancel }: { onDone(): void; onCancel(): void }) {
  const [resource, setResource] = useState("energy");
  const [tier, setTier] = useState<string>("1h");
  const [price, setPrice] = useState("");
  const [mode, setMode] = useState<"live_then_static" | "static_only">("live_then_static");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  return (
    <MutationForm
      title="Publish a floor version"
      description="Centi-SUN per unit — the 0.01 SUN step pricing works in. 2,000 is 20.00 SUN."
      changes={[
        { label: "resource / tier", before: null, after: `${resource} / ${tier}` },
        {
          label: "price_centi_sun_per_unit",
          before: null,
          after: price === "" ? "—" : `${price} (${centiSunToSun(Number(price))})`,
        },
        { label: "mode", before: null, after: mode },
      ]}
      effect={
        effectiveFrom === ""
          ? "At once — the floor has no lead-time rule; it may not be backdated more than 60 s."
          : `${effectiveFrom.replace("T", " ")} UTC`
      }
      submitLabel="Publish floor"
      busy={busy}
      disabled={price === ""}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        setBusy(true);
        setError(null);
        void adminWrites
          .publishFloor({
            resource,
            tier,
            price_centi_sun_per_unit: Number(price),
            mode,
            ...(effectiveFrom === "" ? {} : { effective_from: fromUtcInput(effectiveFrom) ?? "" }),
            reason,
          })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <Field label="Resource" error={fieldError(error, "resource")}>
        <select
          class={inputClass}
          name="resource"
          value={resource}
          onChange={(event) => setResource((event.currentTarget as HTMLSelectElement).value)}
        >
          <option value="energy">energy</option>
          <option value="bandwidth">bandwidth</option>
        </select>
      </Field>
      <Field label="Tier" error={fieldError(error, "tier")}>
        <select
          class={inputClass}
          name="tier"
          value={tier}
          onChange={(event) => setTier((event.currentTarget as HTMLSelectElement).value)}
        >
          {TIERS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </Field>
      <Field
        label="Floor (centi-SUN per unit)"
        hint="2,000 = 20.00 SUN. An integer; the schema takes no float."
        error={fieldError(error, "price_centi_sun_per_unit")}
      >
        <input
          class={inputClass}
          name="price_centi_sun_per_unit"
          type="number"
          min={1}
          value={price}
          onInput={(event) => setPrice((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field label="Mode" error={fieldError(error, "mode")}>
        <select
          class={inputClass}
          name="mode"
          value={mode}
          onChange={(event) =>
            setMode((event.currentTarget as HTMLSelectElement).value as typeof mode)
          }
        >
          <option value="live_then_static">live_then_static</option>
          <option value="static_only">static_only</option>
        </select>
      </Field>
      <Field label="Effective from (UTC, optional)" error={fieldError(error, "effective_from")}>
        <input
          class={inputClass}
          name="effective_from"
          type="datetime-local"
          value={effectiveFrom}
          onInput={(event) => setEffectiveFrom((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
    </MutationForm>
  );
}

// ---- routing fee ------------------------------------------------------------------------------

/** 500 bps -> "5 %", 50 -> "0.5 %". The fee is basis points of the provider price. */
export function feePercent(bps: number): string {
  return `${Number((bps / 100).toFixed(2))} %`;
}

/** The percent typed into the form as basis points, or null when it is not 0..20 in 0.1 steps. */
export function percentToBps(text: string): number | null {
  if (text.trim() === "") return null;
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0 || value > 20) return null;
  const bps = Math.round(value * 100);
  return bps % 10 === 0 ? bps : null;
}

const SUN_PER_TRX = 1_000_000;

/** One form row as typed: spend from in TRX and the fee in percent, both still text. */
interface TierDraft {
  fromTrx: string;
  percent: string;
}

/** The typed rows as the API's tiers, or a sentence saying why they are not one yet. */
export function draftToTiers(rows: TierDraft[]): RouterFeeTier[] | string {
  if (rows.length === 0) return "At least one tier.";
  const tiers: RouterFeeTier[] = [];
  for (const [index, row] of rows.entries()) {
    const trxValue = Number(row.fromTrx);
    if (row.fromTrx.trim() === "" || !Number.isFinite(trxValue) || trxValue < 0) {
      return `Row ${index + 1}: spend from must be a TRX amount, 0 or more.`;
    }
    const feeBps = percentToBps(row.percent);
    if (feeBps === null) return `Row ${index + 1}: fee must be 0 to 20 % in 0.1 steps.`;
    tiers.push({ from_sun: Math.round(trxValue * SUN_PER_TRX), fee_bps: feeBps });
  }
  if (tiers[0]?.from_sun !== 0) return "The first tier starts at 0 TRX.";
  for (let i = 1; i < tiers.length; i += 1) {
    if (tiers[i]!.from_sun <= tiers[i - 1]!.from_sun) return "Spend thresholds must ascend.";
  }
  return tiers;
}

/** "0 → 5 %, 1,000 TRX → 4 %": a whole table on one line, for history rows and the diff. */
function tiersLine(tiers: RouterFeeTier[]): string {
  if (tiers.length === 0) return "none";
  return tiers.map((tier) => `${trx(tier.from_sun, 0)} → ${feePercent(tier.fee_bps)}`).join(", ");
}

function RouterFeePanel({ role }: { role: AdminRole }) {
  const fee = useResource((signal) => {
    void signal;
    return adminReads.routerFeeSchedules();
  }, []);
  const active = fee.data?.active ?? null;

  return (
    <VersionedPanel
      title="Routing fee"
      subtitle="A percent of the provider price, added on top of it, by the account's 30-day spend. A quote carries the fee in force when it was issued; a promo code wins when it is lower."
      role={role}
      formLabel="Change the fee tiers"
      renderForm={(done, cancel) =>
        // The form edits the table in force; opened before it loads it would start from one empty
        // row and publish that as the whole table.
        fee.data == null ? (
          <Spinner />
        ) : (
          <RouterFeeForm
            activeTiers={active?.tiers ?? []}
            onDone={() => {
              fee.reload();
              done();
            }}
            onCancel={cancel}
          />
        )
      }
    >
      {fee.loading ? (
        <Spinner />
      ) : fee.error !== null ? (
        <ErrorNote error={fee.error} onRetry={() => fee.reload()} />
      ) : (
        <>
          <div class="mb-2 text-[11px] text-ink-muted">
            Active{" "}
            {active?.effective_from == null
              ? "— no schedule published yet"
              : `since ${utc(active.effective_from)}`}
          </div>
          <ActiveTiersTable tiers={active?.tiers ?? []} />
          <div class="mt-3 mb-2 text-[11px] text-ink-muted">History</div>
          <RouterFeeHistoryTable rows={fee.data?.versions ?? []} />
        </>
      )}
    </VersionedPanel>
  );
}

function ActiveTiersTable({ tiers }: { tiers: RouterFeeTier[] }) {
  return (
    <Table
      rows={tiers.length}
      empty="No fee schedule has ever been published."
      head={
        <tr>
          <Th numeric>30-day spend from</Th>
          <Th numeric>Fee</Th>
        </tr>
      }
    >
      {tiers.map((tier) => (
        <tr key={tier.from_sun}>
          <Td numeric>
            <span title={`${tier.from_sun} SUN`}>{trx(tier.from_sun, 0)}</span>
          </Td>
          <Td numeric>
            <span title={`${tier.fee_bps} bps`}>{feePercent(tier.fee_bps)}</span>
          </Td>
        </tr>
      ))}
    </Table>
  );
}

function RouterFeeHistoryTable({ rows }: { rows: RouterFeeSchedule[] }) {
  return (
    <Table
      rows={rows.length}
      empty="No fee schedule version has ever been published."
      head={
        <tr>
          <Th>Tiers</Th>
          <Th>Effective from</Th>
          <Th>Changed by</Th>
          <Th>Reason</Th>
        </tr>
      }
    >
      {rows.map((row) => (
        <tr key={row.id}>
          <Td>{tiersLine(row.tiers)}</Td>
          <Td mono>{utc(row.effective_from)}</Td>
          <Td>{row.changed_by === null ? <Blank /> : <Address value={row.changed_by} />}</Td>
          <Td>{row.reason ?? <Blank />}</Td>
        </tr>
      ))}
    </Table>
  );
}

function RouterFeeForm({
  activeTiers,
  onDone,
  onCancel,
}: {
  activeTiers: RouterFeeTier[];
  onDone(): void;
  onCancel(): void;
}) {
  // The form starts from the table in force: a new version is the whole table, edited.
  const [rows, setRows] = useState<TierDraft[]>(() =>
    activeTiers.length === 0
      ? [{ fromTrx: "0", percent: "" }]
      : activeTiers.map((tier) => ({
          fromTrx: String(tier.from_sun / SUN_PER_TRX),
          percent: String(tier.fee_bps / 100),
        })),
  );
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const parsed = draftToTiers(rows);
  const tiers = typeof parsed === "string" ? null : parsed;

  const setRow = (index: number, patch: Partial<TierDraft>) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...patch } : row)));

  return (
    <MutationForm
      title="Change the fee tiers"
      description="The whole table is published as one version. Spend is the account's 30-day spend in TRX; the fee is a percent of the provider price, 0 to 20 in 0.1 steps, sent as basis points (5 % = 500)."
      changes={[
        {
          label: "tiers",
          before: activeTiers.length === 0 ? null : tiersLine(activeTiers),
          after: tiers === null ? "—" : tiersLine(tiers),
        },
      ]}
      effect={
        effectiveFrom === ""
          ? "At once. Quotes issued after this moment carry the new fee; ones already issued keep theirs."
          : `${effectiveFrom.replace("T", " ")} UTC`
      }
      submitLabel="Publish fee tiers"
      busy={busy}
      disabled={tiers === null}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        if (tiers === null) return;
        setBusy(true);
        setError(null);
        void adminWrites
          .publishRouterFeeSchedule({
            tiers,
            ...(effectiveFrom === "" ? {} : { effective_from: fromUtcInput(effectiveFrom) ?? "" }),
            reason,
          })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <div class="space-y-2" data-testid="router-fee-tiers">
        {rows.map((row, index) => (
          <div key={index} class="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
            <Field label={`Spend from, TRX (row ${index + 1})`}>
              <input
                class={inputClass}
                name={`tier_${index}_from_trx`}
                type="number"
                min={0}
                step="any"
                value={row.fromTrx}
                disabled={index === 0}
                onInput={(event) => setRow(index, { fromTrx: (event.currentTarget as HTMLInputElement).value })}
              />
            </Field>
            <Field label={`Fee, % (row ${index + 1})`}>
              <input
                class={inputClass}
                name={`tier_${index}_percent`}
                type="number"
                min={0}
                max={20}
                step={0.1}
                value={row.percent}
                onInput={(event) => setRow(index, { percent: (event.currentTarget as HTMLInputElement).value })}
              />
            </Field>
            <Button
              tone="ghost"
              disabled={rows.length === 1}
              onClick={() => setRows((current) => current.filter((_, at) => at !== index))}
            >
              Remove
            </Button>
          </div>
        ))}
        <Button tone="ghost" onClick={() => setRows((current) => [...current, { fromTrx: "", percent: "" }])}>
          Add a tier
        </Button>
        {typeof parsed === "string" ? (
          <p class="text-[11px] text-danger" role="alert">
            {parsed}
          </p>
        ) : null}
        {fieldError(error, "tiers") === null ? null : (
          <p class="text-[11px] text-danger" role="alert">
            {fieldError(error, "tiers")}
          </p>
        )}
      </div>
      <Field label="Effective from (UTC, optional)" error={fieldError(error, "effective_from")}>
        <input
          class={inputClass}
          name="effective_from"
          type="datetime-local"
          value={effectiveFrom}
          onInput={(event) => setEffectiveFrom((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
    </MutationForm>
  );
}

// ---- transfer price ---------------------------------------------------------------------------

function TransferPanel({ role }: { role: AdminRole }) {
  const transfer = useResource((signal) => {
    void signal;
    return adminReads.transferPrices({ limit: 50 });
  }, []);
  const activeBp = transfer.data?.active?.sun_per_energy_day_bp ?? null;

  return (
    <VersionedPanel
      title="Transfer price"
      subtitle="What the platform pays the fund per unit of capacity per day. An order settles at the price active when it was allocated."
      role={role}
      formLabel="Publish a transfer price"
      renderForm={(done, cancel) => (
        <TransferForm
          activeBp={activeBp}
          onDone={() => {
            transfer.reload();
            done();
          }}
          onCancel={cancel}
        />
      )}
    >
      {transfer.loading ? (
        <Spinner />
      ) : transfer.error !== null ? (
        <ErrorNote error={transfer.error} onRetry={() => transfer.reload()} />
      ) : (
        <>
          <div class="mb-2 text-[11px] text-ink-muted">
            Active:{" "}
            <span class="text-ink">
              {activeBp === null ? "none — nothing settles until one is published" : bpToSun(activeBp)}
            </span>
            {activeBp === null ? null : (
              <span class="ml-1" title={`${activeBp} bp of one SUN`}>
                ({count(activeBp)} bp)
              </span>
            )}
          </div>
          <TransferTable rows={transfer.data?.versions ?? []} />
        </>
      )}
    </VersionedPanel>
  );
}

function TransferTable({ rows }: { rows: TransferVersion[] }) {
  return (
    <Table
      rows={rows.length}
      empty="No transfer price has ever been published. Nothing the fund supplies can be settled until one is."
      head={
        <tr>
          <Th>Effective from</Th>
          <Th>Scope</Th>
          <Th numeric>Price</Th>
          <Th>Changed by</Th>
          <Th>Reason</Th>
        </tr>
      }
    >
      {rows.map((row) => (
        <tr key={row.id}>
          <Td mono>{utc(row.effective_from)}</Td>
          <Td mono>{row.fund_wallet_id ?? <span class="text-ink-muted">global</span>}</Td>
          <Td numeric>
            <span title={`${row.sun_per_energy_day_bp} bp of one SUN`}>
              {bpToSun(row.sun_per_energy_day_bp)}
            </span>
          </Td>
          <Td>{row.changed_by === null ? <Blank /> : <Address value={row.changed_by} />}</Td>
          <Td>{row.reason ?? <Blank />}</Td>
        </tr>
      ))}
    </Table>
  );
}

function TransferForm({
  activeBp,
  onDone,
  onCancel,
}: {
  activeBp: number | null;
  onDone(): void;
  onCancel(): void;
}) {
  const [bp, setBp] = useState("");
  const [wallet, setWallet] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  return (
    <MutationForm
      title="Publish a transfer price"
      description="Basis points of one SUN per unit per day. The measured rate on 2026-09-18 was 2.9 SUN, which is 29,000 bp — the scale exists because that is not a whole number of SUN."
      changes={[
        {
          label: "sun_per_energy_day_bp",
          before: activeBp === null ? null : `${activeBp} (${bpToSun(activeBp)})`,
          after: bp === "" ? "—" : `${bp} (${bpToSun(Number(bp))})`,
        },
        { label: "scope", before: null, after: wallet === "" ? "global" : wallet },
      ]}
      effect={
        effectiveFrom === ""
          ? "At once. Orders allocated after this moment settle at the new price; ones already allocated keep the old one."
          : `${effectiveFrom.replace("T", " ")} UTC`
      }
      submitLabel="Publish transfer price"
      busy={busy}
      disabled={bp === ""}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        setBusy(true);
        setError(null);
        void adminWrites
          .publishTransfer({
            sun_per_energy_day_bp: Number(bp),
            ...(wallet === "" ? {} : { fund_wallet_id: wallet }),
            ...(effectiveFrom === "" ? {} : { effective_from: fromUtcInput(effectiveFrom) ?? "" }),
            reason,
          })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <Field
        label="Transfer price (bp of one SUN)"
        hint="29,000 = 2.9 SUN per unit per day."
        error={fieldError(error, "sun_per_energy_day_bp")}
      >
        <input
          class={inputClass}
          name="sun_per_energy_day_bp"
          type="number"
          min={0}
          value={bp}
          onInput={(event) => setBp((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Fund wallet (optional)"
        hint="Empty means the global price every wallet without a row of its own settles at."
        error={fieldError(error, "fund_wallet_id")}
      >
        <input
          class={inputClass}
          name="fund_wallet_id"
          value={wallet}
          onInput={(event) => setWallet((event.currentTarget as HTMLInputElement).value.trim())}
        />
      </Field>
      <Field label="Effective from (UTC, optional)" error={fieldError(error, "effective_from")}>
        <input
          class={inputClass}
          name="effective_from"
          type="datetime-local"
          value={effectiveFrom}
          onInput={(event) => setEffectiveFrom((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
    </MutationForm>
  );
}

// ---- ladder parameters -------------------------------------------------------------------------

function LadderPanel({ role }: { role: AdminRole }) {
  const ladder = useResource((signal) => {
    void signal;
    return adminReads.ladderParameters({ limit: 50 });
  }, []);
  const active = ladder.data?.active ?? null;

  return (
    <VersionedPanel
      title="Ladder parameters"
      subtitle="The ask ladder's bands, margins, hold-back and caps (order-book.md §2). These ARE prices: a surcharge band is a price rise and a margin is a price."
      role={role}
      formLabel="Publish ladder parameters"
      renderForm={(done, cancel) => (
        <LadderForm
          active={active}
          onDone={() => {
            ladder.reload();
            done();
          }}
          onCancel={cancel}
        />
      )}
    >
      {ladder.loading ? (
        <Spinner />
      ) : ladder.error !== null ? (
        <ErrorNote error={ladder.error} onRetry={() => ladder.reload()} />
      ) : (
        <>
          {active === null ? (
            <div class="mb-2">
              <Note>
                Nothing published. The platform runs on the neutral values — no bands, no margin,
                no hold-back — which prices provider levels at cost and own capacity at today's
                retail price. That is a state, not a gap in this panel.
              </Note>
            </div>
          ) : null}
          <LadderTable rows={ladder.data?.versions ?? []} />
        </>
      )}
    </VersionedPanel>
  );
}

function LadderTable({ rows }: { rows: LadderVersion[] }) {
  return (
    <Table
      rows={rows.length}
      empty="No ladder version has ever been published."
      head={
        <tr>
          <Th>Effective from</Th>
          <Th>Bands</Th>
          <Th numeric>Margin instant</Th>
          <Th numeric>Margin market</Th>
          <Th numeric>Margin deep</Th>
          <Th numeric>Hold-back</Th>
          <Th numeric>Min level</Th>
          <Th numeric>Max levels</Th>
          <Th numeric>Slippage</Th>
          <Th>Changed by</Th>
          <Th>Reason</Th>
        </tr>
      }
    >
      {rows.map((row) => (
        <tr key={row.id}>
          <Td mono>{utc(row.effective_from)}</Td>
          <Td>
            {row.utilisation_bands === null || row.utilisation_bands.length === 0 ? (
              <Blank title="No bands: own capacity is priced flat" />
            ) : (
              row.utilisation_bands
                .map((band) => `>${band.above_bps / 100}% +${band.surcharge_bps / 100}%`)
                .join(", ")
            )}
          </Td>
          <Td numeric>{bps(row.margin_instant_bp)}</Td>
          <Td numeric>{bps(row.margin_market_bp)}</Td>
          <Td numeric>{bps(row.margin_deep_bp)}</Td>
          <Td numeric>{bps(row.hold_back_bp)}</Td>
          <Td numeric>{count(row.min_level_amount)}</Td>
          <Td numeric>{count(row.max_levels)}</Td>
          <Td numeric>{bps(row.slippage_bp)}</Td>
          <Td>{row.changed_by === null ? <Blank /> : <Address value={row.changed_by} />}</Td>
          <Td>{row.reason ?? <Blank />}</Td>
        </tr>
      ))}
    </Table>
  );
}

function LadderForm({
  active,
  onDone,
  onCancel,
}: {
  active: LadderVersion | null;
  onDone(): void;
  onCancel(): void;
}) {
  const [marginInstant, setMarginInstant] = useState("");
  const [marginMarket, setMarginMarket] = useState("");
  const [marginDeep, setMarginDeep] = useState("");
  const [holdBack, setHoldBack] = useState("");
  const [slippage, setSlippage] = useState("");
  const [bandsText, setBandsText] = useState("");
  const [bandsError, setBandsError] = useState<string | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const bands = parseBands(bandsText);
  const changes = [
    ...numberChange("margin_instant_bp", active?.margin_instant_bp ?? null, marginInstant),
    ...numberChange("margin_market_bp", active?.margin_market_bp ?? null, marginMarket),
    ...numberChange("margin_deep_bp", active?.margin_deep_bp ?? null, marginDeep),
    ...numberChange("hold_back_bp", active?.hold_back_bp ?? null, holdBack),
    ...numberChange("slippage_bp", active?.slippage_bp ?? null, slippage),
    ...(bandsText.trim() === ""
      ? []
      : [
          {
            label: "utilisation_bands",
            before:
              active?.utilisation_bands === undefined || active?.utilisation_bands === null
                ? null
                : JSON.stringify(active.utilisation_bands),
            after: bands === null ? "invalid" : JSON.stringify(bands),
          },
        ]),
  ];

  return (
    <MutationForm
      title="Publish ladder parameters"
      description="A version states what it changes; anything it leaves out keeps the neutral value — no band, no margin, no hold-back. Nothing here is seeded, and none of these numbers has a default."
      changes={changes}
      effect={
        effectiveFrom === ""
          ? "At once. The next ladder rebuild (a few seconds) uses it."
          : `${effectiveFrom.replace("T", " ")} UTC`
      }
      submitLabel="Publish parameters"
      busy={busy}
      disabled={changes.length === 0 || bandsError !== null}
      error={error}
      reason={reason}
      onReason={setReason}
      onCancel={onCancel}
      onSubmit={() => {
        setBusy(true);
        setError(null);
        void adminWrites
          .publishLadder({
            ...(marginInstant === "" ? {} : { margin_instant_bp: Number(marginInstant) }),
            ...(marginMarket === "" ? {} : { margin_market_bp: Number(marginMarket) }),
            ...(marginDeep === "" ? {} : { margin_deep_bp: Number(marginDeep) }),
            ...(holdBack === "" ? {} : { hold_back_bp: Number(holdBack) }),
            ...(slippage === "" ? {} : { slippage_bp: Number(slippage) }),
            ...(bands === null || bands.length === 0 ? {} : { utilisation_bands: bands }),
            ...(effectiveFrom === "" ? {} : { effective_from: fromUtcInput(effectiveFrom) ?? "" }),
            reason,
          })
          .then(onDone)
          .catch(setError)
          .finally(() => setBusy(false));
      }}
    >
      <Field label="Margin, instant (bp)" error={fieldError(error, "margin_instant_bp")}>
        <input
          class={inputClass}
          name="margin_instant_bp"
          type="number"
          min={0}
          value={marginInstant}
          onInput={(event) => setMarginInstant((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field label="Margin, market (bp)" error={fieldError(error, "margin_market_bp")}>
        <input
          class={inputClass}
          name="margin_market_bp"
          type="number"
          min={0}
          value={marginMarket}
          onInput={(event) => setMarginMarket((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field label="Margin, deep (bp)" error={fieldError(error, "margin_deep_bp")}>
        <input
          class={inputClass}
          name="margin_deep_bp"
          type="number"
          min={0}
          value={marginDeep}
          onInput={(event) => setMarginDeep((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Hold-back (bp of own capacity)"
        hint="Kept back from the public ladder for contracts. 10,000 would hold everything back."
        error={fieldError(error, "hold_back_bp")}
      >
        <input
          class={inputClass}
          name="hold_back_bp"
          type="number"
          min={0}
          max={10_000}
          value={holdBack}
          onInput={(event) => setHoldBack((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Slippage tolerance (bp)"
        hint="0 means an order is refunded rather than filled at a worse price."
        error={fieldError(error, "slippage_bp")}
      >
        <input
          class={inputClass}
          name="slippage_bp"
          type="number"
          min={0}
          value={slippage}
          onInput={(event) => setSlippage((event.currentTarget as HTMLInputElement).value)}
        />
      </Field>
      <Field
        label="Utilisation bands"
        hint="One per line, `above_bps surcharge_bps`. 7000 1500 means: above 70 % used, add 15 %."
        error={bandsError ?? fieldError(error, "utilisation_bands")}
      >
        <textarea
          class={`${inputClass} min-h-16 py-1`}
          name="utilisation_bands"
          value={bandsText}
          onInput={(event) => {
            const value = (event.currentTarget as HTMLTextAreaElement).value;
            setBandsText(value);
            setBandsError(value.trim() === "" || parseBands(value) !== null ? null : "Each line must be two whole numbers: above_bps surcharge_bps");
          }}
        />
      </Field>
    </MutationForm>
  );
}

function numberChange(
  label: string,
  before: number | null,
  raw: string,
): Array<{ label: string; before: string | null; after: string }> {
  if (raw.trim() === "") return [];
  return [{ label, before: before === null ? null : String(before), after: raw.trim() }];
}

/** `above_bps surcharge_bps` per line. Null when a line is not two whole numbers. */
export function parseBands(text: string): Array<{ above_bps: number; surcharge_bps: number }> | null {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "");
  if (lines.length === 0) return [];
  const bands: Array<{ above_bps: number; surcharge_bps: number }> = [];
  for (const line of lines) {
    const parts = line.split(/[\s,]+/);
    if (parts.length !== 2) return null;
    const above = Number(parts[0]);
    const surcharge = Number(parts[1]);
    if (!Number.isInteger(above) || !Number.isInteger(surcharge)) return null;
    if (above < 0 || above > 10_000 || surcharge < 0) return null;
    bands.push({ above_bps: above, surcharge_bps: surcharge });
  }
  return bands;
}
